import { useState, useEffect, useMemo } from "react";
import { useAuth } from "../contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Calendar } from "../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { toast } from "sonner";
import { Plus, CalendarIcon, Trash2, Pencil } from "lucide-react";
import { format, parseISO } from "date-fns";
import axios from "axios";

const STATUS_OPTIONS = [
  { value: "confermata", label: "Confermata", cls: "bg-green-50 text-green-700 border-green-200" },
  { value: "completata", label: "Completata", cls: "bg-stone-100 text-stone-600 border-stone-200" },
  { value: "cancellata", label: "Cancellata", cls: "bg-red-50 text-red-600 border-red-200" },
];

const PROVENIENZA_OPTIONS = [
  { value: "booking", label: "Booking.com", cls: "bg-blue-50 text-blue-700 border-blue-200" },
  { value: "airbnb", label: "Airbnb", cls: "bg-rose-50 text-rose-700 border-rose-200" },
  { value: "diretto", label: "Diretto", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
];

function calculateNet(total, platformFee, cleaningFee, variableCosts, sconti, rimborsi, provenienza) {
  if (provenienza === "airbnb") return total + cleaningFee - sconti - platformFee;
  if (provenienza === "booking") return total - platformFee - variableCosts - cleaningFee - sconti;
  return total - cleaningFee - variableCosts; // diretto
}

function fmtDate(dateStr) {
  if (!dateStr) return "";
  try { return format(parseISO(dateStr), "dd/MM/yyyy"); } catch { return dateStr; }
}

function DatePicker({ value, onChange, label, testId }) {
  const [open, setOpen] = useState(false);
  const date = value ? parseISO(value) : undefined;
  return (
    <div>
      <Label className="text-xs text-stone-600">{label}</Label>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button variant="outline" data-testid={testId} className={`w-full justify-start text-left font-normal mt-1 bg-white ${!value ? "text-stone-400" : ""}`}>
            <CalendarIcon className="mr-2 h-4 w-4" />
            {value ? fmtDate(value) : "Seleziona data"}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar mode="single" selected={date} onSelect={(d) => { if (d) { onChange(format(d, "yyyy-MM-dd")); setOpen(false); } }} initialFocus />
        </PopoverContent>
      </Popover>
    </div>
  );
}

const emptyForm = { property_id: "", guest_name: "", check_in: "", check_out: "", total_amount: 0, platform_fee: 0, cleaning_fee: 0, variable_costs: 0, sconti: 0, rimborsi: 0, status: "confermata", notes: "", provenienza: "diretto" };

export default function BookingsPage() {
  const { user, API } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [filterProp, setFilterProp] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [calendarProp, setCalendarProp] = useState("all");

  const isAdmin = user?.role === "admin";

  useEffect(() => {
    Promise.all([axios.get(`${API}/bookings`), axios.get(`${API}/properties`)])
      .then(([b, p]) => { setBookings(b.data); setProperties(p.data); if (p.data.length > 0) setCalendarProp(p.data[0].id); })
      .catch(() => toast.error("Errore nel caricamento"))
      .finally(() => setLoading(false));
  }, [API]);

  const filtered = useMemo(() => {
    let result = bookings;
    if (filterProp !== "all") result = result.filter(b => b.property_id === filterProp);
    if (filterStatus !== "all") result = result.filter(b => b.status === filterStatus);
    return result;
  }, [bookings, filterProp, filterStatus]);

  const calendarDates = useMemo(() => {
    const dates = [];
    const propBookings = calendarProp === "all" ? bookings : bookings.filter(b => b.property_id === calendarProp);
    propBookings.filter(b => b.status !== "cancellata").forEach(b => {
      try { const start = parseISO(b.check_in); const end = parseISO(b.check_out); let d = new Date(start); while (d <= end) { dates.push(new Date(d)); d.setDate(d.getDate() + 1); } } catch {}
    });
    return dates;
  }, [bookings, calendarProp]);

  const openAdd = () => { setEditing(null); setForm(emptyForm); setDialogOpen(true); };
  const openEdit = (b) => { setEditing(b); setForm({ property_id: b.property_id, guest_name: b.guest_name, check_in: b.check_in, check_out: b.check_out, total_amount: b.total_amount, platform_fee: b.platform_fee, cleaning_fee: b.cleaning_fee, variable_costs: b.variable_costs || 0, sconti: b.sconti || 0, rimborsi: b.rimborsi || 0, status: b.status, notes: b.notes || "", provenienza: b.provenienza || "diretto" }); setDialogOpen(true); };

  const handleSave = async () => {
    const net = calculateNet(Number(form.total_amount), Number(form.platform_fee), Number(form.cleaning_fee), Number(form.variable_costs), Number(form.sconti), Number(form.rimborsi), form.provenienza);
    const payload = { ...form, net_amount: net, total_amount: Number(form.total_amount), platform_fee: Number(form.platform_fee), cleaning_fee: Number(form.cleaning_fee), variable_costs: Number(form.variable_costs), sconti: Number(form.sconti), rimborsi: Number(form.rimborsi) };
    try {
      if (editing) {
        const res = await axios.put(`${API}/bookings/${editing.id}`, payload);
        setBookings(prev => prev.map(b => b.id === editing.id ? res.data : b));
        toast.success("Prenotazione aggiornata");
      } else {
        const res = await axios.post(`${API}/bookings`, payload);
        setBookings(prev => [res.data, ...prev]);
        toast.success("Prenotazione aggiunta");
      }
      setDialogOpen(false);
    } catch (err) { toast.error(err.response?.data?.detail || "Errore"); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Eliminare questa prenotazione?")) return;
    try { await axios.delete(`${API}/bookings/${id}`); setBookings(prev => prev.filter(b => b.id !== id)); toast.success("Prenotazione eliminata"); } catch { toast.error("Errore"); }
  };

  const statusBadge = (s) => { const opt = STATUS_OPTIONS.find(o => o.value === s); return <Badge variant="outline" className={`text-[10px] ${opt?.cls || ""}`}>{opt?.label || s}</Badge>; };
  const currentCalProp = properties.find(p => p.id === calendarProp);

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-2 border-stone-300 border-t-stone-900" /></div>;

  return (
    <div className="p-6 lg:p-8 max-w-7xl mx-auto" data-testid="bookings-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl text-stone-900">Prenotazioni</h1>
          <p className="text-stone-500 mt-1 text-sm">{bookings.length} prenotazioni totali</p>
        </div>
        {isAdmin && <Button onClick={openAdd} data-testid="add-booking-button" className="bg-stone-900 hover:bg-stone-800 text-white"><Plus className="h-4 w-4 mr-2" /> Nuova</Button>}
      </div>

      <div className="grid lg:grid-cols-4 gap-4 mb-6">
        <div className="lg:col-span-3">
          <div className="flex gap-3 mb-4 flex-wrap">
            <Select value={filterProp} onValueChange={setFilterProp}>
              <SelectTrigger data-testid="filter-property-select" className="w-48 bg-white"><SelectValue placeholder="Proprietà" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutte le proprietà</SelectItem>
                {properties.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger data-testid="filter-status-select" className="w-40 bg-white"><SelectValue placeholder="Stato" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti gli stati</SelectItem>
                {STATUS_OPTIONS.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <Card className="border border-stone-200 shadow-sm overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-stone-50">
                  <TableHead className="text-xs font-medium text-stone-500">Ospite</TableHead>
                  <TableHead className="text-xs font-medium text-stone-500">Proprietà</TableHead>
                  <TableHead className="text-xs font-medium text-stone-500">OTA</TableHead>
                  <TableHead className="text-xs font-medium text-stone-500">Check-in</TableHead>
                  <TableHead className="text-xs font-medium text-stone-500">Check-out</TableHead>
                  <TableHead className="text-xs font-medium text-stone-500 text-right">Netto Op.</TableHead>
                  <TableHead className="text-xs font-medium text-stone-500">Stato</TableHead>
                  {isAdmin && <TableHead className="text-xs w-20" />}
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.length === 0 ? (
                  <TableRow><TableCell colSpan={8} className="text-center text-stone-400 py-8">Nessuna prenotazione</TableCell></TableRow>
                ) : filtered.map(b => (
                  <TableRow key={b.id} data-testid={`booking-row-${b.id}`} className="hover:bg-stone-50 transition-colors">
                    <TableCell className="text-sm font-medium text-stone-900">{b.guest_name}</TableCell>
                    <TableCell className="text-sm text-stone-600">{b.property_name}</TableCell>
                    <TableCell>{(() => { const opt = PROVENIENZA_OPTIONS.find(o => o.value === b.provenienza); return <Badge variant="outline" className={`text-[10px] ${opt?.cls || "bg-stone-50 text-stone-500"}`}>{opt?.label || b.provenienza || "N/A"}</Badge>; })()}</TableCell>
                    <TableCell className="text-sm font-mono text-stone-600">{fmtDate(b.check_in)}</TableCell>
                    <TableCell className="text-sm font-mono text-stone-600">{fmtDate(b.check_out)}</TableCell>
                    <TableCell className="text-sm font-mono text-stone-900 text-right font-medium">{"\u20AC"}{(b.netto_operativo || b.net_amount || 0).toLocaleString("it-IT", {minimumFractionDigits: 2})}</TableCell>
                    <TableCell>{statusBadge(b.status)}</TableCell>
                    {isAdmin && (
                      <TableCell>
                        <div className="flex gap-1">
                          <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => openEdit(b)}><Pencil className="h-3.5 w-3.5 text-stone-500" /></Button>
                          <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => handleDelete(b.id)}><Trash2 className="h-3.5 w-3.5 text-red-500" /></Button>
                        </div>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </div>

        <div>
          <Card className="border border-stone-200 shadow-sm">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-stone-900">Calendario</CardTitle>
              <Select value={calendarProp} onValueChange={setCalendarProp}>
                <SelectTrigger data-testid="calendar-property-select" className="mt-1 bg-white text-xs h-8"><SelectValue placeholder="Seleziona appartamento" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutti gli appartamenti</SelectItem>
                  {properties.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </CardHeader>
            <CardContent className="p-2" data-testid="bookings-calendar">
              {currentCalProp && <p className="text-[11px] font-medium text-stone-600 px-2 mb-1">{currentCalProp.name}</p>}
              <Calendar
                mode="single"
                modifiers={{ booked: calendarDates }}
                modifiersClassNames={{ booked: "bg-amber-100 text-amber-900 font-semibold rounded-sm" }}
                className="rounded-sm"
              />
              <p className="text-[10px] text-stone-400 mt-2 px-2 flex items-center gap-1.5">
                <span className="inline-block w-3 h-3 rounded-sm bg-amber-100 border border-amber-200" /> Giorni con prenotazione
              </p>
            </CardContent>
          </Card>
        </div>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto" data-testid="booking-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">{editing ? "Modifica Prenotazione" : "Nuova Prenotazione"}</DialogTitle>
            <DialogDescription className="text-sm text-stone-500">{editing ? "Modifica i dettagli della prenotazione" : "Inserisci i dettagli della nuova prenotazione"}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label className="text-xs text-stone-600">Proprietà</Label>
              <Select value={form.property_id} onValueChange={v => setForm({ ...form, property_id: v })}>
                <SelectTrigger data-testid="booking-property-select" className="mt-1 bg-white"><SelectValue placeholder="Seleziona proprietà" /></SelectTrigger>
                <SelectContent>{properties.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs text-stone-600">Nome Ospite</Label>
                <Input data-testid="booking-guest-input" value={form.guest_name} onChange={e => setForm({ ...form, guest_name: e.target.value })} placeholder="Nome e cognome" className="mt-1 bg-white" />
              </div>
              <div>
                <Label className="text-xs text-stone-600">Provenienza</Label>
                <Select value={form.provenienza} onValueChange={v => setForm({ ...form, provenienza: v })}>
                  <SelectTrigger data-testid="booking-provenienza-select" className="mt-1 bg-white"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {PROVENIENZA_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <DatePicker label="Check-in" value={form.check_in} onChange={v => setForm({ ...form, check_in: v })} testId="booking-checkin-picker" />
              <DatePicker label="Check-out" value={form.check_out} onChange={v => setForm({ ...form, check_out: v })} testId="booking-checkout-picker" />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-xs text-stone-600">Totale ({"\u20AC"})</Label>
                <Input data-testid="booking-total-input" type="number" step="0.01" value={form.total_amount} onChange={e => setForm({ ...form, total_amount: parseFloat(e.target.value) || 0 })} className="mt-1 bg-white" />
              </div>
              <div>
                <Label className="text-xs text-stone-600">Comm. OTA</Label>
                <Input type="number" step="0.01" value={form.platform_fee} onChange={e => setForm({ ...form, platform_fee: parseFloat(e.target.value) || 0 })} className="mt-1 bg-white" />
              </div>
              <div>
                <Label className="text-xs text-stone-600">Pulizie</Label>
                <Input type="number" step="0.01" value={form.cleaning_fee} onChange={e => setForm({ ...form, cleaning_fee: parseFloat(e.target.value) || 0 })} className="mt-1 bg-white" />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-xs text-stone-600">Transazioni</Label>
                <Input data-testid="booking-variable-costs-input" type="number" step="0.01" value={form.variable_costs} onChange={e => setForm({ ...form, variable_costs: parseFloat(e.target.value) || 0 })} className="mt-1 bg-white" />
              </div>
              <div>
                <Label className="text-xs text-stone-600">Sconti</Label>
                <Input data-testid="booking-sconti-input" type="number" step="0.01" value={form.sconti} onChange={e => setForm({ ...form, sconti: parseFloat(e.target.value) || 0 })} className="mt-1 bg-white" />
              </div>
              <div>
                <Label className="text-xs text-stone-600">Rimborsi</Label>
                <Input data-testid="booking-rimborsi-input" type="number" step="0.01" value={form.rimborsi} onChange={e => setForm({ ...form, rimborsi: parseFloat(e.target.value) || 0 })} className="mt-1 bg-white" />
              </div>
            </div>
            <div className="flex items-center gap-2 p-3 bg-stone-50 rounded-sm border border-stone-200">
              <span className="text-xs text-stone-500">Netto operativo:</span>
              <span className="font-mono text-sm font-semibold text-stone-900">{"\u20AC"}{calculateNet(Number(form.total_amount), Number(form.platform_fee), Number(form.cleaning_fee), Number(form.variable_costs), Number(form.sconti), Number(form.rimborsi), form.provenienza).toFixed(2)}</span>
              <span className="text-[10px] text-stone-400 ml-auto">
                {form.provenienza === "airbnb" && "Tot + Pulizie - Sconti - OTA"}
                {form.provenienza === "booking" && "Tot - OTA - Trans. - Pulizie - Sconti"}
                {form.provenienza === "diretto" && "Tot - Pulizie - Trans."}
              </span>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs text-stone-600">Stato</Label>
                <Select value={form.status} onValueChange={v => setForm({ ...form, status: v })}>
                  <SelectTrigger className="mt-1 bg-white"><SelectValue /></SelectTrigger>
                  <SelectContent>{STATUS_OPTIONS.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-stone-600">Note</Label>
                <Input value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} placeholder="Note opzionali" className="mt-1 bg-white" />
              </div>
            </div>
            <Button onClick={handleSave} data-testid="booking-save-button" className="w-full bg-stone-900 hover:bg-stone-800 text-white">
              {editing ? "Salva Modifiche" : "Aggiungi Prenotazione"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
